import React, { useEffect, useState, useCallback } from 'react';
import DashboardShell from '@/components/DashboardShell';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle, XCircle, MoreHorizontal, Eye } from 'lucide-react';
import { toast } from 'sonner';
import AdminTable from '@/components/AdminTable';

const STATUS_BADGE = {
  pending: { label: 'Pending', style: { backgroundColor: '#FEF3C7', color: '#92400E' } },
  approved: { label: 'Approved', style: { backgroundColor: '#D1FAE5', color: '#065F46' } },
  rejected: { label: 'Rejected', style: { backgroundColor: '#FEE2E2', color: '#991B1B' } },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function Applications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [reviewDialog, setReviewDialog] = useState(null); // { user, action }
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [detailUser, setDetailUser] = useState(null);

  const fetchApplications = useCallback((status) => {
    setLoading(true);
    const params = status === 'all' ? '' : `?status=${status}`;
    api.get(`/admin/applications${params}`)
      .then(r => setApplications(r.data))
      .catch(() => toast.error('Failed to load applications'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchApplications(filter); }, [filter, fetchApplications]);

  const openReview = (user, action) => {
    setReviewDialog({ user, action });
    setNotes('');
  };

  const submitReview = async () => {
    if (!reviewDialog) return;
    setSubmitting(true);
    try {
      await api.post(`/admin/applications/${reviewDialog.user.id}/review`, {
        action: reviewDialog.action,
        admin_notes: notes,
      });
      toast.success(`Application ${reviewDialog.action} successfully`);
      setReviewDialog(null);
      fetchApplications(filter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardShell title="Applications">
      <div className="space-y-6">
        {/* Filter tabs */}
        <div className="flex items-center justify-between">
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList style={{ backgroundColor: 'var(--bg-subtle)' }}>
              <TabsTrigger value="pending">Pending</TabsTrigger>
              <TabsTrigger value="approved">Approved</TabsTrigger>
              <TabsTrigger value="rejected">Rejected</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>
          </Tabs>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{applications.length} result{applications.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Table */}
        <AdminTable
          data={applications}
          loading={loading}
          dataTestId="admin-applications-table"
          emptyTitle={`No ${filter === 'all' ? '' : filter} applications`}
          emptySubtitle="Applications will appear here once submitted."
          columns={[
            {
              key: 'applicant', label: 'Applicant',
              render: (app) => (
                <div>
                  <p className="font-medium" style={{ color: 'var(--text)' }}>{app.name}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{app.email}</p>
                </div>
              ),
            },
            { key: 'applied', label: 'Applied', render: (app) => <span style={{ color: 'var(--text-muted)' }}>{formatDate(app.membership_applied_at)}</span> },
            { key: 'role', label: 'Role', render: (app) => <span className="text-xs capitalize" style={{ color: 'var(--text-muted)' }}>{app.seller_type || '—'}</span> },
            {
              key: 'status', label: 'Status',
              render: (app) => {
                const badge = STATUS_BADGE[app.membership_status] || STATUS_BADGE.pending;
                return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium" style={badge.style}>{badge.label}</span>;
              },
            },
            {
              key: 'actions', label: '', width: '60px',
              render: (app) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal size={15} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
                    <DropdownMenuItem onClick={() => setDetailUser(app)} className="gap-2 cursor-pointer">
                      <Eye size={14} /> View details
                    </DropdownMenuItem>
                    {app.membership_status === 'pending' && (
                      <>
                        <DropdownMenuItem
                          onClick={() => openReview(app, 'approved')}
                          className="gap-2 cursor-pointer"
                          style={{ color: 'var(--success)' }}
                          data-testid="admin-approve-button"
                        >
                          <CheckCircle size={14} /> Approve
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openReview(app, 'rejected')}
                          className="gap-2 cursor-pointer"
                          style={{ color: 'var(--danger)' }}
                          data-testid="admin-reject-button"
                        >
                          <XCircle size={14} /> Reject
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            },
          ]}
        />
      </div>

      {/* Review Confirmation Dialog */}
      <Dialog open={!!reviewDialog} onOpenChange={() => setReviewDialog(null)}>
        <DialogContent style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>
              {reviewDialog?.action === 'approved' ? 'Approve' : 'Reject'} application
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {reviewDialog?.action === 'approved'
                ? `${reviewDialog?.user?.name} will be notified and granted member access.`
                : `${reviewDialog?.user?.name} will be notified that their application was not successful.`
              }
            </p>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
                Admin notes {reviewDialog?.action === 'rejected' ? '(required)' : '(optional)'}
              </label>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add a note for the applicant…"
                rows={3}
                className="text-sm"
                style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setReviewDialog(null)} disabled={submitting}
              style={{ borderRadius: '10px' }}>
              Cancel
            </Button>
            <Button
              onClick={submitReview}
              disabled={submitting || (reviewDialog?.action === 'rejected' && !notes.trim())}
              style={{
                backgroundColor: reviewDialog?.action === 'approved' ? 'var(--success)' : 'var(--danger)',
                color: 'white',
                borderRadius: '10px',
              }}
            >
              {submitting ? 'Processing…' : reviewDialog?.action === 'approved' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailUser} onOpenChange={() => setDetailUser(null)}>
        <DialogContent style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Applicant details</DialogTitle>
          </DialogHeader>
          {detailUser && (
            <div className="space-y-3 text-sm">
              {[
                ['Name', detailUser.name],
                ['Email', detailUser.email],
                ['Seller type', detailUser.seller_type || '—'],
                ['Location', detailUser.location || '—'],
                ['Bio', detailUser.bio || '—'],
                ['Applied', formatDate(detailUser.membership_applied_at)],
                ['Status', detailUser.membership_status],
                ['Admin notes', detailUser.membership_admin_notes || '—'],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-4">
                  <span className="w-28 flex-shrink-0 font-medium" style={{ color: 'var(--text-muted)' }}>{k}</span>
                  <span style={{ color: 'var(--text)' }}>{v}</span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDetailUser(null)} style={{ borderRadius: '10px' }}>Close</Button>
            {detailUser?.membership_status === 'pending' && (
              <>
                <Button
                  onClick={() => { setDetailUser(null); openReview(detailUser, 'approved'); }}
                  style={{ backgroundColor: 'var(--success)', color: 'white', borderRadius: '10px' }}
                  data-testid="admin-approve-button"
                >
                  Approve
                </Button>
                <Button
                  onClick={() => { setDetailUser(null); openReview(detailUser, 'rejected'); }}
                  style={{ backgroundColor: 'var(--danger)', color: 'white', borderRadius: '10px' }}
                  data-testid="admin-reject-button"
                >
                  Reject
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
